<template>
  <div class="bb-dealer">
    <div class="content">
      <h1 class="title">Kies deler</h1>
      <p class="hint">De deler biedt als laatste en schuift elke ronde één plek op.</p>

      <BbPlayerGrid class="seats" :count="playerNames.length" role="radiogroup" aria-label="Deler">
        <div
          v-for="(name, index) in playerNames"
          :key="index"
          class="seat"
          :class="{ selected: selectedIndex === index }"
          tabindex="0"
          role="radio"
          :aria-checked="selectedIndex === index"
          @click="selectedIndex = index"
          @keydown.enter.stop="select(index)"
          @keydown.space.prevent="selectedIndex = index"
        >
          <span class="player-label">Speler {{ index + 1 }}</span>
          <span class="player-name">{{ name }}</span>
        </div>
      </BbPlayerGrid>

      <button class="random-btn" @click="pickRandom">
        <span class="random-icon">⇄</span>
        <span class="random-text">Willekeurig</span>
      </button>
    </div>

    <AppFooter
      :can-continue="selectedIndex !== null"
      :show-back="true"
      @continue="confirm"
      @back="setView('setup')"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia'
import { useBoerenbridgeStore } from '../../stores/boerenbridge'
import BbPlayerGrid from './BbPlayerGrid.vue'
import AppFooter from '../shared/AppFooter.vue'

export default {
  name: 'BbDealerSelect',
  components: { BbPlayerGrid, AppFooter },
  data() {
    return { selectedIndex: null }
  },
  computed: {
    ...mapState(useBoerenbridgeStore, ['playerNames'])
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    ...mapActions(useBoerenbridgeStore, ['confirmDealer', 'setView']),
    select(index) {
      this.selectedIndex = index
      this.confirm()
    },
    pickRandom() {
      this.selectedIndex = Math.floor(Math.random() * this.playerNames.length)
    },
    confirm() {
      if (this.selectedIndex === null) return
      this.confirmDealer(this.selectedIndex)
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && this.selectedIndex !== null) this.confirm()
      if (e.key === 'Escape') this.setView('setup')
    }
  }
}
</script>

<style scoped>
.bb-dealer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-y: contain;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: safe center;
  padding: 1.25rem;
  gap: 1rem;
}

.title {
  font-size: 1.75rem;
  color: #2c3e50;
  text-align: center;
}

.hint {
  font-size: 0.85rem;
  color: #5a6d5a;
  text-align: center;
}

.seats {
  max-width: min(420px, 100%);
}

.seat {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 1rem 1.25rem;
  border: 2px solid #aac8aa;
  border-radius: 8px;
  background: #fff;
  color: #2c3e50;
  cursor: pointer;
  touch-action: manipulation;
  transition: border-color 0.15s, background 0.15s;
  text-align: left;
  min-width: 0;
}

.seat.selected {
  border-color: #2c3e50;
  background: #2c3e50;
  color: #fff;
}

.player-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.6;
}

.player-name {
  font-size: 1.15rem;
  font-weight: 600;
}

.random-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  width: 100%;
  max-width: min(420px, 100%);
  padding: 0.75rem;
  font-size: 1rem;
  font-family: inherit;
  background: transparent;
  color: #2c3e50;
  border: 2px solid #2c3e50;
  border-radius: 8px;
  cursor: pointer;
  touch-action: manipulation;
}

.random-btn:active { opacity: 0.75; }

.random-icon { font-size: 1.1rem; }

/* Op tablet staan de spelers als tegels in een raster dat bij hun aantal
   past (2×2 bij vier, 3×3 bij negen). */
@media (min-width: 600px) {
  .content {
    padding: 1.5rem;
    gap: 1.25rem;
  }

  .title { font-size: 2rem; }

  .hint { font-size: 1rem; }

  .seats,
  .random-btn {
    max-width: 48rem;
  }

  .seat {
    min-height: 6rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    border-radius: 14px;
  }

  .player-label { font-size: 0.85rem; }

  .player-name {
    max-width: 100%;
    font-size: 1.5rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .random-btn {
    padding: 0.9rem;
    font-size: 1.1rem;
  }
}

@media (hover: hover) {
  .seat:hover:not(.selected) { border-color: #7a9a7a; }
  .random-btn:hover { background: rgba(44, 62, 80, 0.05); }
}
</style>
